import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, apiError, formatINR } from "@/lib/api";
import { LandingNav, LandingFooter } from "@/components/landing/LandingSections";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Loader2, Search, CalendarDays, Stethoscope, IndianRupee, Hash } from "lucide-react";

const STATUS = {
  pending: "bg-amber-100 text-amber-800 border-amber-200",
  confirmed: "bg-teal-100 text-teal-800 border-teal-200",
  completed: "bg-emerald-100 text-emerald-800 border-emerald-200",
  cancelled: "bg-rose-100 text-rose-800 border-rose-200",
};
const STATUS_TEXT = {
  pending: "Awaiting confirmation from the clinic", confirmed: "Your appointment is confirmed",
  completed: "Visit completed", cancelled: "This appointment was cancelled",
};
const fmt = (s) => new Date(s).toLocaleString("en-IN", { dateStyle: "full", timeStyle: "short" });

const Row = ({ icon: Icon, label, children }) => (
  <div className="flex items-start gap-3 py-3 border-b border-slate-100 last:border-0">
    <Icon className="h-4 w-4 text-teal-600 mt-1 shrink-0" />
    <div><div className="text-xs uppercase tracking-wider text-slate-400">{label}</div><div className="font-medium text-slate-900">{children}</div></div>
  </div>
);

export default function TrackBooking() {
  const [params] = useSearchParams();
  const [ref, setRef] = useState(params.get("ref") || "");
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const search = async () => {
    if (!ref.trim() || phone.replace(/\D/g, "").length < 10) return toast.error("Enter your reference ID and the mobile number used to book");
    setLoading(true); setResult(null);
    try { setResult((await api.get("/public/track", { params: { ref: ref.trim(), phone } })).data); }
    catch (e) { toast.error(apiError(e, "Booking not found")); } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <LandingNav />
      <main className="max-w-xl mx-auto px-5 sm:px-8 py-14 animate-fade-up">
        <div className="text-xs font-medium uppercase tracking-wider text-teal-600 mb-3">Track Appointment</div>
        <h1 className="font-head text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-900">Where's my appointment?</h1>
        <p className="mt-3 text-slate-500">Enter the reference ID you received and the mobile number you booked with.</p>

        <div className="mt-8 bg-white rounded-3xl border border-slate-200 p-6 space-y-4" data-testid="track-form">
          <div className="space-y-2"><Label>Reference ID</Label><Input data-testid="track-ref-input" className="h-12 font-mono uppercase" value={ref} onChange={(e) => setRef(e.target.value.toUpperCase())} placeholder="SJC-XXXXXX" /></div>
          <div className="space-y-2"><Label>Mobile Number</Label><Input data-testid="track-phone-input" className="h-12" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="10-digit mobile number" onKeyDown={(e) => e.key === "Enter" && search()} /></div>
          <Button data-testid="track-submit" onClick={search} disabled={loading} className="w-full h-12 bg-teal-600 hover:bg-teal-700 text-white text-base">
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Search className="h-4 w-4 mr-2" /> Find Appointment</>}
          </Button>
        </div>

        {result && (
          <div className="mt-6 bg-white rounded-3xl border border-slate-200 p-6 animate-fade-up" data-testid="track-result">
            <div className="flex items-center justify-between gap-3 mb-2">
              <div className="font-head text-xl font-bold text-slate-900">{result.patient_name}</div>
              <Badge variant="outline" className={`capitalize ${STATUS[result.status] || ""}`} data-testid="track-status">{result.status}</Badge>
            </div>
            <p className="text-sm text-slate-500 mb-3">{STATUS_TEXT[result.status]}</p>
            <Row icon={Hash} label="Reference ID"><span className="font-mono text-teal-700">{result.reference_id}</span></Row>
            <Row icon={Stethoscope} label="Doctor">{result.doctor_name} <span className="text-slate-500 font-normal">· {result.doctor_specialty}</span></Row>
            <Row icon={CalendarDays} label="Date & Time">{fmt(result.appointment_date)}</Row>
            <Row icon={IndianRupee} label="Consultation Fee"><span className="font-mono">{formatINR(result.amount)}</span> <span className="text-xs text-slate-400 font-normal">payable at clinic</span></Row>
          </div>
        )}
        <p className="mt-6 text-sm text-slate-500">Need a new appointment? <Link to="/book" className="text-teal-700 font-medium hover:underline" data-testid="book-link">Book online</Link></p>
      </main>
      <LandingFooter />
    </div>
  );
}
